import React, { useEffect, useRef, useState } from 'react';
import { useAssistantChat, type LocalizationLike } from './useAssistantChat';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { Dock, Header, IconButton, Notice, NoticeAction, NoticeText, Root, Title } from './styled';
import { CheckIcon, ChevronDownIcon, CloseIcon, TrashIcon } from './icons';

const CLEAR_CONFIRM_TIMEOUT_MS = 2500;
const COLLAPSED_MAX_ROWS = 3;
const EXPANDED_MAX_ROWS = 6;

interface AssistantChatProps {
    localization: LocalizationLike;
    projectId: string;
}

/**
 * Assistant chat docked at the bottom of the trainer.
 * Collapsed: only the composer is visible, so a question can be typed or dictated from any tab.
 * Expanded: takes over the trainer column — header, scrollable messages, composer pinned at the bottom.
 */
export function AssistantChat({ localization, projectId }: AssistantChatProps) {
    const chat = useAssistantChat({ localization, projectId });
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClearConfirming, setIsClearConfirming] = useState(false);
    const clearConfirmTimeoutRef = useRef<number | null>(null);
    const t = (key: string) => localization.getText(`assistant_chat.${key}`);

    useEffect(() => {
        return () => {
            if (clearConfirmTimeoutRef.current) window.clearTimeout(clearConfirmTimeoutRef.current);
        };
    }, []);

    const submit = () => {
        if (!chat.canSend) return;
        setIsExpanded(true);
        void chat.sendMessage();
    };

    const toggleClear = () => {
        if (clearConfirmTimeoutRef.current) {
            window.clearTimeout(clearConfirmTimeoutRef.current);
            clearConfirmTimeoutRef.current = null;
        }
        if (isClearConfirming) {
            chat.clearConversation();
            setIsClearConfirming(false);
            return;
        }
        setIsClearConfirming(true);
        clearConfirmTimeoutRef.current = window.setTimeout(() => {
            setIsClearConfirming(false);
            clearConfirmTimeoutRef.current = null;
        }, CLEAR_CONFIRM_TIMEOUT_MS);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Escape') return;
        if (chat.voice.status !== 'idle') {
            event.preventDefault();
            chat.voice.cancel();
        } else if (isExpanded) {
            event.preventDefault();
            setIsExpanded(false);
        }
    };

    const clearLabel = isClearConfirming ? t('clear_confirm_button') : t('clear_button');

    return (
        <Root
            $expanded={isExpanded}
            data-testid="assistant-chat"
            data-expanded={isExpanded ? 'true' : 'false'}
            aria-label={t('title')}
            onKeyDown={onKeyDown}>
            {isExpanded && (
                <>
                    <Header>
                        <IconButton
                            type="button"
                            onClick={() => setIsExpanded(false)}
                            title={t('collapse_button')}
                            aria-label={t('collapse_button')}>
                            <ChevronDownIcon size={18} />
                        </IconButton>
                        <Title>{t('title')}</Title>
                        <IconButton
                            type="button"
                            $tone={isClearConfirming ? 'success' : 'default'}
                            onClick={toggleClear}
                            disabled={chat.history.length === 0}
                            title={clearLabel}
                            aria-label={clearLabel}>
                            {isClearConfirming ? <CheckIcon size={16} /> : <TrashIcon size={16} />}
                        </IconButton>
                    </Header>
                    <MessageList
                        localization={localization}
                        history={chat.history}
                        isSending={chat.isSending}
                        isInitializing={chat.isInitializing}
                    />
                </>
            )}

            <Dock>
                {chat.error && (
                    <Notice role="alert" data-testid="assistant-chat-error">
                        <NoticeText>{chat.error.message}</NoticeText>
                        {chat.error.kind === 'init' ? (
                            <NoticeAction type="button" onClick={chat.retryInit}>
                                {t('retry')}
                            </NoticeAction>
                        ) : (
                            <IconButton type="button" onClick={chat.dismissError} title={t('dismiss')} aria-label={t('dismiss')}>
                                <CloseIcon size={14} />
                            </IconButton>
                        )}
                    </Notice>
                )}
                <Composer
                    localization={localization}
                    value={chat.input}
                    onChange={chat.setInput}
                    onSubmit={submit}
                    canSend={chat.canSend}
                    isReady={chat.isReady}
                    isInitializing={chat.isInitializing}
                    maxRows={isExpanded ? EXPANDED_MAX_ROWS : COLLAPSED_MAX_ROWS}
                    voice={chat.voice}
                    onExpand={isExpanded ? undefined : () => setIsExpanded(true)}
                    autoFocus={isExpanded}
                />
            </Dock>
        </Root>
    );
}
